import { Button } from '@/components/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcn/dialog'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { useRestClient } from '@/contexts/QueryProvider'
import { useCurrentApplication } from '../useCurrentApplication'
import { FormEvent, useState } from 'react'
import { toast } from 'sonner'
import { DEFAULT_MENU_NAME, serializeCustomMenuDefinition, toMenuKeySlug } from './utils'

type CreateCustomMenuDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingMenuKeys: string[]
  getCustomMenuApiPathParam: (menuKey: string, menuName?: string) => string
  onCreated: (menuKey: string, menuName: string) => void
}

export function CreateCustomMenuDialog({
  open,
  onOpenChange,
  existingMenuKeys,
  getCustomMenuApiPathParam,
  onCreated,
}: CreateCustomMenuDialogProps) {
  const { applicationId } = useCurrentApplication()
  const { postUiApiSaveCustomMenu, getUiApiGetCustomMenus } = useRestClient()
  const { mutateAsync: saveCustomMenu, isPending } = postUiApiSaveCustomMenu.useMutation()

  const [menuKey, setMenuKey] = useState('')
  const [menuName, setMenuName] = useState('')
  const [menuKeyTouched, setMenuKeyTouched] = useState(false)
  const [keyError, setKeyError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const resetForm = () => {
    setMenuKey('')
    setMenuName('')
    setMenuKeyTouched(false)
    setKeyError(null)
    setNameError(null)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm()
    }
    onOpenChange(nextOpen)
  }

  const handleNameChange = (value: string) => {
    setMenuName(value)
    setNameError(null)
    if (!menuKeyTouched) {
      setMenuKey(toMenuKeySlug(value))
      setKeyError(null)
    }
  }

  const handleKeyChange = (value: string) => {
    setMenuKey(value)
    setMenuKeyTouched(true)
    setKeyError(null)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()

    const trimmedKey = menuKey.trim()
    const trimmedName = menuName.trim()

    let valid = true

    if (!trimmedKey) {
      setKeyError('Key is required.')
      valid = false
    } else if (trimmedKey === DEFAULT_MENU_NAME.toLowerCase()) {
      setKeyError(`"${DEFAULT_MENU_NAME}" is reserved for the default menu.`)
      valid = false
    } else if (existingMenuKeys.some((k) => k.toLowerCase() === trimmedKey.toLowerCase())) {
      setKeyError('A custom menu with this key already exists.')
      valid = false
    }

    if (!trimmedName) {
      setNameError('Name is required.')
      valid = false
    }

    if (!valid) return

    try {
      await saveCustomMenu({
        path: { applicationId, menuKey: getCustomMenuApiPathParam(trimmedKey, trimmedName) },
        body: { definition: serializeCustomMenuDefinition(trimmedKey, trimmedName, []) },
      })
      await getUiApiGetCustomMenus.invalidateQueries({ parameters: { path: { applicationId } } })
      toast.success(`Custom menu "${trimmedName}" created.`)
      onCreated(trimmedKey, trimmedName)
      handleOpenChange(false)
    } catch {
      toast.error('Failed to create custom menu. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create custom menu</DialogTitle>
            <DialogDescription>
              Give the new menu a key and a display name. You can add menu items after it is created.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="custom-menu-name">Display name</Label>
              <Input
                id="custom-menu-name"
                value={menuName}
                onChange={(event) => handleNameChange(event.target.value)}
                placeholder="e.g. Admin menu"
                autoFocus
              />
              {nameError ? <p className="text-sm text-destructive">{nameError}</p> : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="custom-menu-key">
                Key{' '}
                <span className="text-muted-foreground font-normal">
                  (unique identifier, auto-filled from name)
                </span>
              </Label>
              <Input
                id="custom-menu-key"
                value={menuKey}
                onChange={(event) => handleKeyChange(event.target.value)}
                placeholder="e.g. admin-menu"
              />
              {keyError ? <p className="text-sm text-destructive">{keyError}</p> : null}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Creating...' : 'Create menu'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
